import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, X, Filter, Clock, CheckCircle2, AlertCircle, ChevronRight, MessageCircle, Tag, RefreshCw } from '../DeskeyIcons';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { notifyError } from '../../lib/errors';
import { confirmDialog } from '../ConfirmDialog';
import { institutionApi } from '../../lib/institutionApi';
import Loader from '../Loader';

const api = institutionApi;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '--';

const STATUS_STYLES = {
  open: 'bg-blue-50 text-blue-600 border-blue-100',
  in_progress: 'bg-amber-50 text-amber-600 border-amber-100',
  resolved: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  closed: 'bg-gray-50 text-gray-600 border-gray-100',
};

const PRIORITY_STYLES = {
  low: 'bg-gray-50 text-gray-500 border-gray-100',
  medium: 'bg-amber-50 text-amber-500 border-amber-100',
  high: 'bg-orange-50 text-orange-500 border-orange-100',
  urgent: 'bg-red-50 text-red-500 border-red-100',
};

const TYPE_LABELS = {
  software_issue: 'Software Issue',
  billing_question: 'Billing Question',
  account_issue: 'Account Issue',
  feature_request: 'Feature Request',
  other: 'Other',
};

const TICKET_TYPES = Object.entries(TYPE_LABELS);
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

/* ── Status Badge ── */
function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold border ${STATUS_STYLES[status] || STATUS_STYLES.open}`}>
      {(status || 'open').replace('_', ' ').toUpperCase()}
    </span>
  );
}

/* ── Priority Badge ── */
function PriorityBadge({ priority }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium}`}>
      {(priority || 'medium').toUpperCase()}
    </span>
  );
}

/* ── Create Ticket Modal ── */
function CreateTicketModal({ accent, onCreate, onClose }) {
  const [form, setForm] = useState({ title: '', type: 'software_issue', priority: 'medium', description: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    setLoading(true);
    try {
      await onCreate(form);
      onClose();
    } catch (e) {
      notifyError(e);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="create-support-ticket-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Raise Support Ticket</h3>
            <p className="text-xs text-gray-500 mt-0.5">Tell us how we can help</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-700">Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Brief summary of your issue"
              className="w-full h-10 mt-1 px-3 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent bg-white"
              style={{ '--tw-ring-color': `${accent}40` }}
              data-testid="support-ticket-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full h-10 mt-1 px-3 text-sm rounded-lg border border-gray-200 bg-white"
                data-testid="support-ticket-type"
              >
                {TICKET_TYPES.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full h-10 mt-1 px-3 text-sm rounded-lg border border-gray-200 bg-white"
                data-testid="support-ticket-priority"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={5}
              placeholder="Describe your issue in detail. Include any error messages, steps to reproduce, or relevant context."
              className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent bg-white resize-none"
              style={{ '--tw-ring-color': `${accent}40` }}
              data-testid="support-ticket-description"
            />
          </div>
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 h-10 rounded-xl text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 h-10 rounded-xl text-sm font-semibold text-white"
            style={{ background: accent }}
            data-testid="submit-support-ticket"
          >
            {loading ? 'Submitting...' : 'Submit Ticket'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── Ticket Detail Modal ── */
function TicketDetailModal({ ticket, accent, onClose, onRefresh }) {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleAddComment = async () => {
    if (!comment.trim()) { toast.error('Comment cannot be empty'); return; }
    setSubmitting(true);
    try {
      await api(`/institution/support-tickets/${ticket.id}`, {
        method: 'PUT',
        body: JSON.stringify({ text: comment, close_ticket: false }),
      });
      toast.success('Comment added');
      setComment('');
      onRefresh();
    } catch (e) { notifyError(e); }
    setSubmitting(false);
  };

  const handleClose = async () => {
    if (!(await confirmDialog({ title: 'Close ticket', message: 'Are you sure you want to close this ticket?', danger: false, confirmLabel: 'Close ticket' }))) return;
    setClosing(true);
    try {
      await api(`/institution/support-tickets/${ticket.id}`, {
        method: 'PUT',
        body: JSON.stringify({ text: 'Ticket closed by institution.', close_ticket: true }),
      });
      toast.success('Ticket closed');
      onRefresh();
      onClose();
    } catch (e) { notifyError(e); }
    setClosing(false);
  };

  const comments = ticket.comments || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ticket-detail-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 h-[600px] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-start justify-between gap-4 z-10 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 font-medium">
                {TYPE_LABELS[ticket.type] || ticket.type}
              </span>
            </div>
            <h3 className="text-base font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{ticket.title}</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">Created {fmtDate(ticket.created_at)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center flex-shrink-0">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Description */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Description</p>
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          </div>

          {/* Resolution Notes */}
          {ticket.resolution_notes && (
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">Resolution</p>
              <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
                <p className="text-sm text-emerald-800 whitespace-pre-wrap">{ticket.resolution_notes}</p>
              </div>
            </div>
          )}

          {/* Messages Thread */}
          {comments.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Messages ({comments.filter((c) => !c.is_internal).length})
              </p>
              <div className="space-y-3">
                {comments
                  .filter((c) => !c.is_internal)
                  .map((c) => {
                    const isOps = ['super_admin', 'zone_admin', 'state_manager', 'sales_rep', 'support_admin'].includes(c.user_role);
                    return (
                      <div
                        key={c.id}
                        className={`flex gap-3 ${isOps ? 'flex-row' : 'flex-row-reverse'}`}
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                          style={{ background: isOps ? '#34A853' : accent }}
                        >
                          {isOps ? 'OPS' : 'YOU'}
                        </div>
                        <div className={`max-w-[80%] ${isOps ? '' : 'text-right'}`}>
                          <div
                            className={`px-4 py-2.5 rounded-2xl text-sm ${
                              isOps
                                ? 'bg-gray-100 text-gray-800 rounded-tl-sm'
                                : 'text-white rounded-tr-sm'
                            }`}
                            style={!isOps ? { background: accent } : {}}
                          >
                            {c.text}
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1 px-1">{fmtDateTime(c.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Fixed Bottom Section - Input + Footer */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white">
          {/* Add Comment Input */}
          {!['closed', 'resolved'].includes(ticket.status) && (
            <div className="px-6 py-3 border-b border-gray-100">
              <div className="flex gap-2">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  placeholder="Add a message or provide more details..."
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent bg-white resize-none"
                  style={{ '--tw-ring-color': `${accent}40` }}
                  data-testid="ticket-comment-input"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={submitting}
                  className="h-auto px-4 py-2 text-xs font-bold rounded-lg text-white self-end"
                  style={{ background: accent }}
                  data-testid="submit-comment"
                >
                  {submitting ? '...' : 'Send'}
                </Button>
              </div>
            </div>
          )}

          {/* Footer Actions */}
          <div className="px-6 py-3 flex items-center justify-between">
            <p className="text-[11px] text-gray-400 font-mono">#{ticket.id?.slice(0, 8).toUpperCase()}</p>
            <div className="flex gap-2">
              {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                <button
                  onClick={handleClose}
                  disabled={closing}
                  className="h-9 px-4 text-xs font-semibold rounded-xl text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
                  data-testid="close-ticket-btn"
                >
                  {closing ? 'Closing...' : 'Close Ticket'}
                </button>
              )}
              <button onClick={onClose} className="h-9 px-4 text-xs font-semibold rounded-xl text-white transition-colors" style={{ background: accent }}>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* ███  MAIN COMPONENT  ███ */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function SupportTicketsSection({ accent }) {
  const A = accent || '#34A853';
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/institution/support-tickets');
      setTickets(res.tickets || []);
    } catch (e) {
      toast.error('Failed to load support tickets');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    await api('/institution/support-tickets', { method: 'POST', body: JSON.stringify(data) });
    toast.success('Support ticket raised! Our team will respond shortly.');
    load();
  };

  const handleSelectTicket = async (t) => {
    try {
      const full = await api(`/institution/support-tickets/${t.id}`);
      setSelectedTicket(full);
    } catch {
      setSelectedTicket(t);
    }
  };

  const handleRefreshSelected = async () => {
    if (!selectedTicket) return;
    try {
      const full = await api(`/institution/support-tickets/${selectedTicket.id}`);
      setSelectedTicket(full);
    } catch {}
    load();
  };

  const filtered = tickets.filter((t) => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (t.title || '').toLowerCase().includes(s) || (t.description || '').toLowerCase().includes(s);
    }
    return true;
  });

  // Stats
  const stats = {
    open: tickets.filter((t) => t.status === 'open').length,
    in_progress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    closed: tickets.filter((t) => t.status === 'closed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-120px)]" data-testid="support-tickets-loading">
        <Loader label="Loading support tickets..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-120px)]" data-testid="support-tickets-section">
      {showCreate && (
        <CreateTicketModal
          accent={A}
          onCreate={handleCreate}
          onClose={() => setShowCreate(false)}
        />
      )}
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          accent={A}
          onClose={() => setSelectedTicket(null)}
          onRefresh={handleRefreshSelected}
        />
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0" data-testid="support-stat-cards">
        {[
          { label: 'Open', value: stats.open, icon: AlertCircle, color: 'blue' },
          { label: 'In Progress', value: stats.in_progress, icon: Clock, color: 'amber' },
          { label: 'Resolved', value: stats.resolved, icon: CheckCircle2, color: 'emerald' },
          { label: 'Closed', value: stats.closed, icon: Tag, color: 'gray' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl shadow p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-2">
              <div className={`w-9 h-9 rounded-xl bg-${color}-50 flex items-center justify-center`}>
                <Icon className={`w-4 h-4 text-${color}-500`} />
              </div>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
            </div>
            <p className="text-xl font-extrabold text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tickets..."
            className="w-full h-9 pl-9 pr-3 text-sm rounded-lg border border-gray-200 bg-white placeholder:text-gray-300"
            data-testid="support-ticket-search"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 text-xs rounded-lg border border-gray-200 px-2.5 bg-white text-gray-700 font-medium"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-9 text-xs rounded-lg border border-gray-200 px-2.5 bg-white text-gray-700 font-medium"
            data-testid="type-filter"
          >
            <option value="all">All Types</option>
            {TICKET_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="h-9 text-xs rounded-lg border border-gray-200 px-2.5 bg-white text-gray-700 font-medium"
            data-testid="priority-filter"
          >
            <option value="all">All Priority</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={load}
            className="h-9 w-9 rounded-lg border border-gray-200 bg-white flex items-center justify-center hover:bg-gray-50"
            data-testid="refresh-tickets"
          >
            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
          </button>
          <Button
            onClick={() => setShowCreate(true)}
            className="h-9 px-3.5 text-xs font-bold rounded-lg text-white gap-1.5"
            style={{ background: A }}
            data-testid="raise-ticket-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Raise Ticket
          </Button>
        </div>
      </div>

      {/* Ticket List */}
      <div className="bg-white border border-gray-200 rounded-xl shadow flex-1 min-h-0 overflow-y-auto" data-testid="tickets-list">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <MessageCircle className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-base font-semibold text-gray-600 mb-1">
              {tickets.length === 0 ? 'No support tickets yet' : 'No matching tickets'}
            </p>
            <p className="text-xs text-gray-500 max-w-xs mb-4">
              {tickets.length === 0
                ? 'Having an issue? Raise a support ticket and our team will assist you.'
                : 'Try changing your filters.'}
            </p>
            {tickets.length === 0 && (
              <Button
                onClick={() => setShowCreate(true)}
                className="h-9 px-4 text-xs font-bold rounded-lg text-white gap-1.5"
                style={{ background: A }}
              >
                <Plus className="w-3.5 h-3.5" />
                Raise Your First Ticket
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {filtered.map((ticket) => (
              <div
                key={ticket.id}
                className="p-5 hover:bg-gray-50/50 transition-colors cursor-pointer"
                onClick={() => handleSelectTicket(ticket)}
                data-testid={`ticket-row-${ticket.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <StatusBadge status={ticket.status} />
                      <PriorityBadge priority={ticket.priority} />
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 font-medium">
                        {TYPE_LABELS[ticket.type] || ticket.type}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 mb-1">{ticket.title}</p>
                    <p className="text-xs text-gray-500 line-clamp-2">{ticket.description}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 text-gray-400">
                    {(ticket.comments || []).filter((c) => !c.is_internal).length > 0 && (
                      <div className="flex items-center gap-1 text-[11px]">
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>{ticket.comments.filter((c) => !c.is_internal).length}</span>
                      </div>
                    )}
                    <ChevronRight className="w-4 h-4 group-hover:text-gray-600" />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
                  <span>Created: {fmtDate(ticket.created_at)}</span>
                  {ticket.resolved_at && <span>Resolved: {fmtDate(ticket.resolved_at)}</span>}
                  <span className="font-mono">#{ticket.id?.slice(0, 8).toUpperCase()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
